// src/features/boe/model/BuildBoeBudgetModal.jsx

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { getDoc, doc } from "firebase/firestore";
import { db } from "../../../firebase.js";
import { XMarkIcon, ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import { CommonTable } from "../../../shared/ui/table/CommonTable.jsx";
import { PROGRAM_CONFIG } from "../../../shared/constants/programBudget.js";

import { buildBuildBudgetColumns } from "../../../budget/model/columns/columnBuilders.js";
import { fmtDollars0 } from "../../../budget/model/workshop/buildBudgetWorkshopUtils.js";

import {
  toYearNumber,
  sumSectionTotals,
  getApprovedTotalWithProposedFallback,
  getDocIdFromProgramKey,
  buildYearAvailability,
  getValidWindowEndingYears,
  isProposedOnlyMode,
  build4ColWindowMeta,
} from "../../../budget/utils/programBudgetUtils.js";

import {
  PROGRAM_0200_CONFIG,
  PROGRAM_0300_CONFIG,
  PROGRAM_0400_CONFIG,
  PROGRAM_0500_CONFIG,
  PROGRAM_0600_CONFIG,
  PROGRAM_0700_CONFIG,
  PROGRAM_0900_CONFIG,
  PROGRAM_1000_CONFIG,
  PROGRAM_1100_CONFIG,
  PROGRAM_1300_CONFIG,
  PROGRAM_1400_CONFIG,
  PROGRAM_1500_CONFIG,
  PROGRAM_1600_CONFIG,
  PROGRAM_1900_CONFIG,
  PROGRAM_2000_CONFIG,
  PROGRAM_2100_CONFIG,
  PROGRAM_2200_CONFIG,
  PROGRAM_2300_CONFIG,
  PROGRAM_2400_CONFIG,
  PROGRAM_2700_CONFIG,
  PROGRAM_2800_CONFIG,
  PROGRAM_3000_CONFIG,
  PROGRAM_3100_CONFIG,
  PROGRAM_3200_CONFIG,
  PROGRAM_3300_CONFIG,
  PROGRAM_3400_CONFIG,
  PROGRAM_3600_CONFIG,
} from "../components/programs/index.js";

// IMPORTANT: xlsx-js-style enables cell styling in browser exports.
import * as XLSX from "xlsx-js-style";

/**
 * Collection where the per-program docs live (e.g., "0200", "0300", "0400", etc.).
 * NOTE: This is the BoE program collection, not townGovernment.
 */
const BOARD_OF_ED_PROGRAMS_COLLECTION = "publicSchools";

/**
 * Tolerance for comparing computed sums (floating / rounding effects).
 * Whole-dollar budgets mean this can be fairly tight.
 */
const MONEY_EPS = 0.5;

/**
 * Maps program document IDs (e.g., "0200") to their configuration objects.
 *
 * @param {string} docId Program doc ID like "0200"
 * @returns {object|null} Program config (PROGRAM_####_CONFIG) or null if unknown
 */
function getProgramConfigByDocId(docId) {
  const id = String(docId || "").trim();

  const map = {
    "0200": PROGRAM_0200_CONFIG,
    "0300": PROGRAM_0300_CONFIG,
    "0400": PROGRAM_0400_CONFIG,
    "0500": PROGRAM_0500_CONFIG,
    "0600": PROGRAM_0600_CONFIG,
    "0700": PROGRAM_0700_CONFIG,
    "0900": PROGRAM_0900_CONFIG,
    1000: PROGRAM_1000_CONFIG,
    1100: PROGRAM_1100_CONFIG,
    1300: PROGRAM_1300_CONFIG,
    1400: PROGRAM_1400_CONFIG,
    1500: PROGRAM_1500_CONFIG,
    1600: PROGRAM_1600_CONFIG,
    1900: PROGRAM_1900_CONFIG,
    2000: PROGRAM_2000_CONFIG,
    2100: PROGRAM_2100_CONFIG,
    2200: PROGRAM_2200_CONFIG,
    2300: PROGRAM_2300_CONFIG,
    2400: PROGRAM_2400_CONFIG,
    2700: PROGRAM_2700_CONFIG,
    2800: PROGRAM_2800_CONFIG,
    3000: PROGRAM_3000_CONFIG,
    3100: PROGRAM_3100_CONFIG,
    3200: PROGRAM_3200_CONFIG,
    3300: PROGRAM_3300_CONFIG,
    3400: PROGRAM_3400_CONFIG,
    3600: PROGRAM_3600_CONFIG,
  };

  return map[id] || null;
}

/** @param {any} v @returns {boolean} */
function isFiniteNumber(v) {
  return typeof v === "number" && Number.isFinite(v);
}

/** @param {any} v @returns {boolean} */
function isPlainObject(v) {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

/**
 * Parse a currency-like input value coming from an <input>.
 * - empty => null
 * - strips $, commas
 * - returns number or null if not finite
 *
 * @param {any} raw
 * @returns {number|null}
 */
function parseCurrencyInput(raw) {
  if (raw == null || raw === "") return null;
  const cleaned = Number(String(raw).replace(/[$,]/g, ""));
  return isFiniteNumber(cleaned) ? cleaned : null;
}

/**
 * Compare monetary values using a tolerance rather than strict equality.
 *
 * @param {number} a
 * @param {number} b
 * @param {number} [eps]
 * @returns {boolean}
 */
function nearlyEqualMoney(a, b, eps = MONEY_EPS) {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= eps;
}

/**
 * Determine whether a Firestore value “looks like” a Timestamp.
 * Used only for debug-safe logging.
 *
 * @param {any} v
 * @returns {boolean}
 */
function isTimestampLike(v) {
  return v && typeof v === "object" && typeof v.toDate === "function";
}

/**
 * Convert a Firestore object into JSON-safe form for logging.
 *
 * @param {any} value
 * @returns {any}
 */
function toSafeJson(value) {
  try {
    return JSON.parse(
      JSON.stringify(value, (key, v) => {
        if (typeof v === "bigint") return v.toString();
        if (isTimestampLike(v)) {
          try {
            return v.toDate().toISOString();
          } catch {
            return "[TimestampLike]";
          }
        }
        if (v instanceof Date) return v.toISOString();
        return v;
      })
    );
  } catch (e) {
    return {
      __warning: "Could not JSON-serialize data for safe logging",
      __error: String(e?.message || e),
    };
  }
}

/**
 * Debug helper for Firestore reads (kept intact for diagnostics).
 *
 * @param {string} tag
 * @param {string} path
 * @param {import("firebase/firestore").DocumentSnapshot} snap
 */
function logFirestoreDocRead(tag, path, snap) {
  const exists = !!snap?.exists?.();
  const rawData = exists ? snap.data() : null;
  const safeData = exists ? toSafeJson(rawData) : null;

  const title = `[BuildBoeBudgetModal][FirestoreRead] ${tag} :: ${path}`;
  console.groupCollapsed(title);
  console.log("path:", path);
  console.log("exists:", exists);
  console.log("rawData (snap.data()):", rawData);
  console.log("safeData (json-safe):", safeData);
  console.groupEnd();
}

/**
 * Select the “baseline” section object for a program for the ending year.
 *
 * @param {object|null} yearObj Program doc year object (e.g. progDoc["2025"])
 * @param {boolean} isProposedMode
 * @returns {object|null}
 */
function pickBaselineYearSection(yearObj, isProposedMode) {
  if (!yearObj) return null;

  if (isProposedMode) return yearObj?.Proposed ?? null;

  if (yearObj?.Approved) return yearObj.Approved;
  if (yearObj?.Adopted) return yearObj.Adopted;
  if (yearObj?.Proposed) return yearObj.Proposed;
  return null;
}

/**
 * Sum configured sources from a section object.
 *
 * @param {object|null} sectionObj
 * @param {Array<{block: string, key: string}>} sources
 * @returns {number|null}
 */
function sumSourcesForSection(sectionObj, sources) {
  if (!sectionObj || !Array.isArray(sources) || sources.length === 0) return null;

  let sum = 0;
  let sawAny = false;

  for (const src of sources) {
    const block = src?.block;
    const key = src?.key;
    if (!block || !key) continue;

    const blockObj =
      block === "Supplies / Materials"
        ? sectionObj?.["Supplies / Materials"]
        : sectionObj?.[block];

    const val = blockObj?.[key];

    if (typeof val === "number" && Number.isFinite(val)) {
      sum += val;
      sawAny = true;
    }
  }

  return sawAny ? sum : null;
}

/**
 * Build flattened detail rows for a specific program, for the 3-year window.
 *
 * @param {object} args
 * @param {object} args.programConfig
 * @param {object|null} args.y2Section
 * @param {object|null} args.y1Section
 * @param {object|null} args.y0Section
 * @param {"y0_adopted"|"y0_proposed"} args.y0Key
 * @returns {Array<object>}
 */
function buildWorkshopFlatRowsFromProgramConfig({
  programConfig,
  y2Section,
  y1Section,
  y0Section,
  y0Key,
}) {
  if (!programConfig || !Array.isArray(programConfig.fieldDefs)) return [];

  const preferredTable =
    programConfig.tables?.find((t) => t?.id === "approved") ||
    programConfig.tables?.find((t) => t?.sectionKey === "Approved") ||
    null;

  const fieldOrder = Array.isArray(preferredTable?.fields)
    ? preferredTable.fields
    : programConfig.fieldDefs.map((d) => d.key);

  const defByKey = new Map(programConfig.fieldDefs.map((d) => [d.key, d]));

  const rowsByBlock = new Map();
  const blockOrder = [];

  for (const fieldKey of fieldOrder) {
    const def = defByKey.get(fieldKey);
    if (!def) continue;
    if (def.group === "positions") continue;

    const v2 = sumSourcesForSection(y2Section, def.sources);
    const v1 = sumSourcesForSection(y1Section, def.sources);
    const v0 = sumSourcesForSection(y0Section, def.sources);

    const hasAny = isFiniteNumber(v2) || isFiniteNumber(v1) || isFiniteNumber(v0);
    if (!hasAny) continue;

    const baselineVal = isFiniteNumber(v0) ? v0 : null;
    const bb = isFiniteNumber(baselineVal) ? Math.round(baselineVal) : null;

    const change = isFiniteNumber(baselineVal) && isFiniteNumber(bb) ? bb - baselineVal : null;

    const pct =
      isFiniteNumber(baselineVal) && baselineVal !== 0 && isFiniteNumber(change)
        ? change / baselineVal
        : null;

    const homeBlock = def?.sources?.[0]?.block || "Other";

    if (!rowsByBlock.has(homeBlock)) {
      rowsByBlock.set(homeBlock, []);
      blockOrder.push(homeBlock);
    }

    rowsByBlock.get(homeBlock).push({
      __rowId: fieldKey,
      label: def.label || def.key,
      y2_actual: isFiniteNumber(v2) ? v2 : null,
      y1_adopted: isFiniteNumber(v1) ? v1 : null,
      ...(y0Key === "y0_adopted" ? { y0_adopted: baselineVal } : { y0_proposed: baselineVal }),
      buildBudget: isFiniteNumber(bb) ? bb : null,
      increase: isFiniteNumber(change) ? change : null,
      percentIncrease: isFiniteNumber(pct) ? pct : null,
    });
  }

  const SECTION_LABELS = {
    Salaries: "SALARIES",
    "Supplies / Materials": "SUPPLIES/MATERIALS",
    Other: "OTHER",
  };

  let order = 0;
  const flat = [];

  for (const block of blockOrder) {
    const rows = rowsByBlock.get(block) || [];
    if (rows.length === 0) continue;

    flat.push({
      section: SECTION_LABELS[block] || String(block || "").toUpperCase(),
      order: order++,
    });

    for (const r of rows) {
      flat.push({ ...r, order: order++ });
    }
  }

  return flat;
}

/**
 * Sum the buildBudget values for detail rows, applying any per-row overrides.
 *
 * @param {Array<object>} flatRows
 * @param {object} detailEditsForDoc
 * @returns {number|null}
 */
function sumDetailBuildBudget(flatRows, detailEditsForDoc) {
  if (!Array.isArray(flatRows)) return null;

  let sum = 0;
  let sawAny = false;

  for (const r of flatRows) {
    if (r?.section) continue;

    const rowId = r?.__rowId ?? r?.label ?? null;
    const override = rowId != null ? detailEditsForDoc?.[String(rowId)] : undefined;

    const v = Number.isFinite(override) ? override : r?.buildBudget;

    if (typeof v === "number" && Number.isFinite(v)) {
      sum += v;
      sawAny = true;
    }
  }

  return sawAny ? sum : null;
}

/**
 * Compute delta and percent change.
 *
 * @param {number|null} baselineVal
 * @param {number|null} buildBudgetVal
 * @returns {{ increase: number|null, percentIncrease: number|null }}
 */
function computeChangeFields(baselineVal, buildBudgetVal) {
  if (!isFiniteNumber(baselineVal) || !isFiniteNumber(buildBudgetVal)) {
    return { increase: null, percentIncrease: null };
  }

  const diff = buildBudgetVal - baselineVal;
  return {
    increase: diff,
    percentIncrease: baselineVal !== 0 ? diff / baselineVal : null,
  };
}

/**
 * Best-effort extraction of text from column labels (string or simple React nodes).
 * This is for Excel headers only.
 *
 * @param {any} node
 * @returns {string}
 */
function stringifyHeader(node) {
  if (node == null) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);

  // React element-ish: try children
  const props = node?.props;
  const children = props?.children;

  if (Array.isArray(children)) {
    return children.map(stringifyHeader).join(" ").replace(/\s+/g, " ").trim();
  }
  if (children != null) {
    return stringifyHeader(children).replace(/\s+/g, " ").trim();
  }

  return "";
}

/**
 * Convert a CSS width like "120px" to an Excel column width (approx chars).
 * Excel width is roughly: chars ~ px / 7 (good enough for matching your table).
 *
 * @param {any} w
 * @param {number} fallback
 * @returns {number}
 */
function pxToExcelWidth(w, fallback = 14) {
  const s = String(w || "");
  const m = s.match(/(\d+)\s*px/i);
  if (!m) return fallback;
  const px = Number(m[1]);
  if (!Number.isFinite(px) || px <= 0) return fallback;
  return Math.max(8, Math.round(px / 7));
}

/**
 * Build and download the workshop as an Excel workbook.
 * - Sheet "Programs": program summary table (same as UI)
 * - Sheet "Details": all accordion (expanded) line items for every program
 *
 * Uses xlsx-js-style so we can apply fills, borders, fonts, number formats.
 *
 * @param {object} args
 * @param {Array<object>} args.columns Column defs from buildBuildBudgetColumns
 * @param {Array<object>} args.rows Rows rendered in main table (includes Total Budget row)
 * @param {string} args.title Modal title
 * @param {number|null} args.endingYear FY start year used in the window
 * @param {"Approved"|"Proposed"} args.y0Status
 * @param {Record<string, any>} args.programDocs Firestore docs keyed by docId
 * @param {boolean} args.isProposedMode Proposed-only mode
 * @param {"y0_adopted"|"y0_proposed"} args.y0Key Baseline key used by the table
 * @param {Record<string, Record<string, number|null>>} args.detailEdits Detail edits map: { [docId]: { [rowId]: number|null } }
 */
function exportWorkshopToExcel({
  columns,
  rows,
  title,
  endingYear,
  y0Status,
  programDocs,
  isProposedMode,
  y0Key,
  detailEdits,
}) {
  const safeTitle = String(title || "Build a BoE Budget Workshop").trim();

  const exportableCols = (columns || []).filter((c) => c?.key);
  const headers = exportableCols.map((c) => stringifyHeader(c?.label || c?.key || ""));

  // ---------------------------
  // Common styling primitives
  // ---------------------------
  const headerFill = "D5E7F7"; // matches your headerColor
  const border = {
    top: { style: "thin", color: { rgb: "BFBFBF" } },
    bottom: { style: "thin", color: { rgb: "BFBFBF" } },
    left: { style: "thin", color: { rgb: "BFBFBF" } },
    right: { style: "thin", color: { rgb: "BFBFBF" } },
  };

  const headerStyle = {
    font: { bold: true },
    alignment: { vertical: "center", horizontal: "center", wrapText: true },
    fill: { patternType: "solid", fgColor: { rgb: headerFill } },
    border,
  };

  const labelStyle = {
    alignment: { vertical: "center", horizontal: "left", wrapText: false },
    border,
  };

  const currencyStyle = {
    alignment: { vertical: "center", horizontal: "right" },
    numFmt: "$#,##0;($#,##0);-",
    border,
  };

  const percentStyle = {
    alignment: { vertical: "center", horizontal: "right" },
    numFmt: "0.0%;-0.0%;-",
    border,
  };

  const numberStyle = {
    alignment: { vertical: "center", horizontal: "right" },
    border,
  };

  const boldRow = (base, fillRgb = "F2F2F2") => ({
    ...base,
    font: { ...(base.font || {}), bold: true },
    fill: { patternType: "solid", fgColor: { rgb: fillRgb } },
  });

  const sectionRowLabelStyle = boldRow(
    {
      alignment: { vertical: "center", horizontal: "left" },
      border,
    },
    "F7F7F7"
  );

  const programHeaderLabelStyle = boldRow(
    {
      alignment: { vertical: "center", horizontal: "left" },
      border,
    },
    "EAEAEA"
  );

  const pickStyleByKey = (key) => {
    if (key === "label") return labelStyle;
    if (key === "percentIncrease") return percentStyle;
    if (
      key === "y2_actual" ||
      key === "y1_adopted" ||
      key === "y0_adopted" ||
      key === "y0_proposed" ||
      key === "buildBudget" ||
      key === "increase"
    ) {
      return currencyStyle;
    }
    return numberStyle;
  };

  // ---------------------------
  // Sheet 1: Programs (summary)
  // ---------------------------
  const programsBody = (rows || []).map((r) =>
    exportableCols.map((c) => {
      const key = c.key;
      const v = r?.[key];

      if (key === "label") return r?.label ?? "";

      if (typeof v !== "number" || !Number.isFinite(v)) return "";
      return v;
    })
  );

  const programsAoa = [headers, ...programsBody];
  const wsPrograms = XLSX.utils.aoa_to_sheet(programsAoa);

  wsPrograms["!cols"] = exportableCols.map((c) => {
    const w = c?.style?.width || c?.style?.minWidth;
    return { wch: pxToExcelWidth(w, c.key === "label" ? 40 : 14) };
  });

  wsPrograms["!freeze"] = { xSplit: 0, ySplit: 1 };

  // Header styling
  for (let c = 0; c < headers.length; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (wsPrograms[addr]) wsPrograms[addr].s = headerStyle;
  }

  // Total row styling
  const totalRowIndexInAoa =
    (rows || []).findIndex((r) => r?.__docId === "__TOTAL__") + 1; // +1 offset for header

  for (let r = 1; r < programsAoa.length; r++) {
    const isTotalRow = totalRowIndexInAoa === r;

    for (let c = 0; c < exportableCols.length; c++) {
      const key = exportableCols[c]?.key;
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = wsPrograms[addr];
      if (!cell) continue;

      const base = pickStyleByKey(key);
      cell.s = isTotalRow ? boldRow(base, "F2F2F2") : base;
    }
  }

  // ---------------------------
  // Sheet 2: Details (all accordions)
  // ---------------------------
  const detailsRows = [];
  const allProgramCfgs = PROGRAM_CONFIG || [];

  // Build a friendly lookup: docId -> program label (from PROGRAM_CONFIG)
  const programLabelByDocId = {};
  for (const cfg of allProgramCfgs) {
    const docId = getDocIdFromProgramKey(cfg?.key);
    if (docId) programLabelByDocId[docId] = cfg?.label || docId;
  }

  // Helper: add a full-width row that visually acts as a "group header"
  const addGroupRow = (text, type /* "program" | "section" */) => {
    const rowArr = exportableCols.map((c) => (c.key === "label" ? text : ""));
    detailsRows.push({ __type: type, aoa: rowArr });
  };

  // Helper: add a detail data row (matches the main schema)
  const addDetailDataRow = (flatRow) => {
    const rowArr = exportableCols.map((c) => {
      const key = c.key;
      const v = flatRow?.[key];

      if (key === "label") return flatRow?.label ?? "";

      if (typeof v !== "number" || !Number.isFinite(v)) return "";
      return v;
    });

    detailsRows.push({ __type: "data", aoa: rowArr });
  };

  // Build all program detail blocks in main-table order
  const orderedDocIds = allProgramCfgs
    .map((cfg) => getDocIdFromProgramKey(cfg?.key))
    .filter(Boolean);

  for (const docId of orderedDocIds) {
    if (docId === "__TOTAL__") continue;

    const progDoc = programDocs?.[docId];
    if (!progDoc || !endingYear) continue;

    const getYearObj = (y) => progDoc?.[String(y)] ?? null;

    const y2Section = getYearObj(endingYear - 2)?.Actual ?? null;
    const y1Section =
      getYearObj(endingYear - 1)?.Approved ??
      getYearObj(endingYear - 1)?.Adopted ??
      null;

    const y0YearObj = getYearObj(endingYear);
    const y0Section = pickBaselineYearSection(y0YearObj, isProposedMode);
    if (!isPlainObject(y0Section)) continue;

    const programConfig = getProgramConfigByDocId(docId);
    if (!programConfig) continue;

    const flatRows = buildWorkshopFlatRowsFromProgramConfig({
      programConfig,
      y2Section,
      y1Section,
      y0Section,
      y0Key,
    });

    if (!Array.isArray(flatRows) || flatRows.length === 0) continue;

    // Program header
    const programLabel = programLabelByDocId[docId] || docId;
    addGroupRow(`${docId} — ${programLabel}`, "program");

    // Apply overrides and recompute change/% relative to baseline (same as UI)
    const detailEditsForDoc = detailEdits?.[docId] || {};

    for (const r of flatRows) {
      if (r?.section) {
        addGroupRow(String(r.section || "").trim(), "section");
        continue;
      }

      const rowId = r?.__rowId ?? r?.label ?? null;
      const override = rowId != null ? detailEditsForDoc[String(rowId)] : undefined;

      const nextBuildBudget = Number.isFinite(override) ? override : r?.buildBudget;
      const baselineVal = r?.[y0Key];

      const { increase, percentIncrease } = computeChangeFields(baselineVal, nextBuildBudget);

      addDetailDataRow({
        ...r,
        // Add a light indent for readability in Excel
        label: `   ${r?.label ?? ""}`,
        buildBudget: Number.isFinite(nextBuildBudget) ? nextBuildBudget : null,
        increase,
        percentIncrease,
      });
    }

    // Blank spacer row between programs
    addGroupRow("", "spacer");
  }

  const detailsAoa = [headers, ...detailsRows.map((x) => x.aoa)];
  const wsDetails = XLSX.utils.aoa_to_sheet(detailsAoa);

  wsDetails["!cols"] = exportableCols.map((c) => {
    const w = c?.style?.width || c?.style?.minWidth;
    return { wch: pxToExcelWidth(w, c.key === "label" ? 55 : 14) };
  });

  wsDetails["!freeze"] = { xSplit: 0, ySplit: 1 };

  // Header styling (Details)
  for (let c = 0; c < headers.length; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (wsDetails[addr]) wsDetails[addr].s = headerStyle;
  }

  // Row styling (Details)
  // detailsRows are offset by 1 because header row is row 0
  for (let i = 0; i < detailsRows.length; i++) {
    const excelRowIndex = i + 1;
    const rowType = detailsRows[i].__type;

    for (let c = 0; c < exportableCols.length; c++) {
      const key = exportableCols[c]?.key;
      const addr = XLSX.utils.encode_cell({ r: excelRowIndex, c });
      const cell = wsDetails[addr];
      if (!cell) continue;

      // Default style based on column
      let base = pickStyleByKey(key);

      if (rowType === "program") {
        // Bold across row, gray fill
        base = boldRow(base, "EAEAEA");
        // Make label cell more "header-like"
        if (key === "label") base = programHeaderLabelStyle;
      } else if (rowType === "section") {
        // Section header rows (only label has text)
        base = boldRow(base, "F7F7F7");
        if (key === "label") base = sectionRowLabelStyle;
      } else if (rowType === "spacer") {
        // No borders/format needed; keep it simple
        base = { alignment: { vertical: "center", horizontal: "left" } };
      }

      cell.s = base;
    }
  }

  // ---------------------------
  // Workbook + filename
  // ---------------------------
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsPrograms, "Programs");
  XLSX.utils.book_append_sheet(wb, wsDetails, "Details");

  const stamp = new Date();
  const yyyy = stamp.getFullYear();
  const mm = String(stamp.getMonth() + 1).padStart(2, "0");
  const dd = String(stamp.getDate()).padStart(2, "0");

  const fy = Number.isFinite(endingYear) ? `${endingYear}-${endingYear + 1}` : "UnknownFY";
  const mode = y0Status === "Proposed" ? "Proposed" : "Approved";
  const filename = `BoE_Budget_Workshop_${fy}_${mode}_${yyyy}-${mm}-${dd}.xlsx`;

  XLSX.writeFile(wb, filename, { compression: true });
}

/**
 * BuildBoeBudgetModal
 *
 * IMPORTANT: Existing functionality and behaviors are preserved.
 */
export function BuildBoeBudgetModal({
  open,
  onClose,
  title = "Build a BoE Budget Workshop",
  initialEndingYear = null,
  town = "Glastonbury",
  millRateDocPath = { collection: "common", id: "millRate" },
  taxRevenuesDocPath = { collection: "townGovernment", id: "revenues_tax_revenues" },
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /** @type {[Record<string, any>, Function]} */
  const [programDocs, setProgramDocs] = useState({});
  const [yearNumbers, setYearNumbers] = useState([]);
  const [endingYear, setEndingYear] = useState(null);

  /** Build-budget edits: { [programLabel]: number|null } */
  const [buildEdits, setBuildEdits] = useState({});

  /** Detail edits: { [docId]: { [rowId]: number|null } } */
  const [detailEdits, setDetailEdits] = useState({});

  /** Expand/collapse per program row: { [docId]: boolean } */
  const [expandedPrograms, setExpandedPrograms] = useState({});

  // Close modal on Escape
  useEffect(() => {
    if (!open) return;

    /** @param {KeyboardEvent} e */
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // Load all program docs when opened
  useEffect(() => {
    if (!open) return;

    let alive = true;

    async function loadAllPrograms() {
      setLoading(true);
      setError("");

      try {
        const docIds = PROGRAM_CONFIG.map((p) => getDocIdFromProgramKey(p.key));

        console.groupCollapsed(
          `[BuildBoeBudgetModal][FirestoreRead] program docs batch :: ${BOARD_OF_ED_PROGRAMS_COLLECTION}/*`
        );
        console.log("collection:", BOARD_OF_ED_PROGRAMS_COLLECTION);
        console.log("docIds:", docIds);
        console.groupEnd();

        const snaps = await Promise.all(
          docIds.map((id) => getDoc(doc(db, BOARD_OF_ED_PROGRAMS_COLLECTION, id)))
        );

        if (!alive) return;

        const docsById = {};
        const yearSet = new Set();

        snaps.forEach((snap, idx) => {
          const id = docIds[idx];
          const path = `${BOARD_OF_ED_PROGRAMS_COLLECTION}/${id}`;

          logFirestoreDocRead("programDoc", path, snap);

          if (!snap.exists()) {
            console.warn(`[BuildBoeBudgetModal] Program doc ${path} not found`);
            return;
          }

          const data = snap.data();
          docsById[id] = data;

          Object.keys(data || {}).forEach((key) => {
            const y = toYearNumber(key);
            if (y) yearSet.add(y);
          });
        });

        setProgramDocs(docsById);
        setYearNumbers(Array.from(yearSet).sort((a, b) => a - b));
      } catch (err) {
        console.error("[BuildBoeBudgetModal] Error loading program docs:", err);
        if (alive) {
          setProgramDocs({});
          setYearNumbers([]);
          setEndingYear(null);
          setError("Failed to load Board of Education program budgets.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadAllPrograms();

    return () => {
      alive = false;
    };
  }, [open]);

  // Availability matrix
  const yearAvail = useMemo(() => buildYearAvailability(programDocs), [programDocs]);

  // Valid ending years
  const endingYearOptions = useMemo(() => {
    if (!yearNumbers.length) return [];
    return getValidWindowEndingYears(yearNumbers, yearAvail);
  }, [yearNumbers, yearAvail]);

  // Initialize ending year
  useEffect(() => {
    if (!open) return;
    if (!endingYearOptions.length) return;

    setEndingYear((prev) => {
      if (prev && endingYearOptions.includes(prev)) return prev;
      if (initialEndingYear && endingYearOptions.includes(initialEndingYear)) return initialEndingYear;
      return endingYearOptions[endingYearOptions.length - 1];
    });
  }, [open, endingYearOptions, initialEndingYear]);

  // Proposed-only mode
  const isProposedMode = useMemo(() => {
    if (!endingYear) return false;
    return isProposedOnlyMode(endingYear, yearAvail);
  }, [endingYear, yearAvail]);

  // 4-col window meta (retained)
  const windowYearsMeta = useMemo(() => build4ColWindowMeta(endingYear, isProposedMode), [
    endingYear,
    isProposedMode,
  ]);

  const y0Status = useMemo(() => (isProposedMode ? "Proposed" : "Approved"), [isProposedMode]);

  const y0Key = useMemo(() => (y0Status === "Proposed" ? "y0_proposed" : "y0_adopted"), [y0Status]);

  // Column definitions
  const { columns } = useMemo(() => {
    if (!endingYear) return { columns: [] };
    return buildBuildBudgetColumns(endingYear, { y0Status });
  }, [endingYear, y0Status]);

  const computeProgramTownKeyTotals = useCallback(
    (progDoc) => {
      const totals = { y2_actual: null, y1_adopted: null, y0_adopted: null, y0_proposed: null };
      if (!progDoc || !endingYear) return totals;

      const safeSum = (section) => {
        const v = sumSectionTotals(section);
        return isFiniteNumber(v) ? v : null;
      };

      const getYearObj = (y) => progDoc?.[String(y)] ?? null;

      totals.y2_actual = safeSum(getYearObj(endingYear - 2)?.Actual);
      totals.y1_adopted = safeSum(getYearObj(endingYear - 1)?.Approved);

      {
        const yearObj = getYearObj(endingYear);
        totals.y0_proposed = safeSum(yearObj?.Proposed);

        if (!isProposedMode) {
          totals.y0_adopted = getApprovedTotalWithProposedFallback(yearObj);
          if (!isFiniteNumber(totals.y0_adopted)) totals.y0_adopted = null;
        }
      }

      return totals;
    },
    [endingYear, isProposedMode]
  );

  const programTableRows = useMemo(() => {
    if (!Object.keys(programDocs).length || !endingYear) return [];

    const rows = [];

    PROGRAM_CONFIG.forEach((cfg) => {
      const docId = getDocIdFromProgramKey(cfg.key);
      const progDoc = programDocs[docId];
      if (!progDoc) return;

      const totals = computeProgramTownKeyTotals(progDoc);

      const hasAny =
        isFiniteNumber(totals.y2_actual) ||
        isFiniteNumber(totals.y1_adopted) ||
        isFiniteNumber(totals.y0_adopted) ||
        isFiniteNumber(totals.y0_proposed);

      if (!hasAny) return;

      const row = {
        label: cfg.label,
        __docId: docId,
        __programLabel: cfg.label,
      };

      if (isFiniteNumber(totals.y2_actual)) row.y2_actual = totals.y2_actual;
      if (isFiniteNumber(totals.y1_adopted)) row.y1_adopted = totals.y1_adopted;
      if (isFiniteNumber(totals.y0_adopted)) row.y0_adopted = totals.y0_adopted;
      if (isFiniteNumber(totals.y0_proposed)) row.y0_proposed = totals.y0_proposed;

      const baselineVal = row?.[y0Key];
      const userEdited = buildEdits?.[row.__programLabel];

      row.buildBudget = isFiniteNumber(userEdited)
        ? userEdited
        : isFiniteNumber(baselineVal)
        ? baselineVal
        : null;

      const { increase, percentIncrease } = computeChangeFields(baselineVal, row.buildBudget);
      row.increase = increase;
      row.percentIncrease = percentIncrease;

      rows.push(row);
    });

    return rows;
  }, [programDocs, endingYear, computeProgramTownKeyTotals, buildEdits, y0Key]);

  const totalBudgetRow = useMemo(() => {
    if (!programTableRows.length) return null;

    const row = {
      label: "Total Budget",
      __docId: "__TOTAL__",
      rowClassName: "font-bold bg-gray-50 border-t-2 border-black",
    };

    const sumKey = (key) =>
      programTableRows.reduce((acc, r) => {
        const v = r?.[key];
        return isFiniteNumber(v) ? acc + v : acc;
      }, 0);

    row.y2_actual = sumKey("y2_actual");
    row.y1_adopted = sumKey("y1_adopted");

    if (y0Key === "y0_adopted") row.y0_adopted = sumKey("y0_adopted");
    else row.y0_proposed = sumKey("y0_proposed");

    row.buildBudget = sumKey("buildBudget");

    const baselineSum = row?.[y0Key];
    const { increase, percentIncrease } = computeChangeFields(baselineSum, row.buildBudget);
    row.increase = increase;
    row.percentIncrease = percentIncrease;

    return row;
  }, [programTableRows, y0Key]);

  const rowsForRendering = useMemo(() => {
    return totalBudgetRow ? [...programTableRows, totalBudgetRow] : programTableRows;
  }, [programTableRows, totalBudgetRow]);

  const hasWindow = endingYear && Array.isArray(columns) && columns.length > 0;

  // Reset expansions when modal opens/window changes
  useEffect(() => {
    if (!open) return;
    setExpandedPrograms({});
  }, [open, endingYear]);

  // Main table edit handler (program-level buildBudget)
  const onChange = useCallback((e, rowLabel, colKey) => {
    if (colKey !== "buildBudget") return;

    const cleaned = parseCurrencyInput(e?.target?.value);

    setBuildEdits((prev) => ({
      ...prev,
      [rowLabel]: isFiniteNumber(cleaned) ? cleaned : null,
    }));
  }, []);

  const programMismatchByDocId = useMemo(() => {
    const map = {};

    for (const row of programTableRows || []) {
      const docId = row?.__docId;
      if (!docId || docId === "__TOTAL__") continue;

      const progDoc = programDocs?.[docId];
      if (!progDoc || !endingYear) continue;

      const getYearObj = (y) => progDoc?.[String(y)] ?? null;

      const y2Section = getYearObj(endingYear - 2)?.Actual ?? null;

      const y1Section =
        getYearObj(endingYear - 1)?.Approved ??
        getYearObj(endingYear - 1)?.Adopted ??
        null;

      const y0YearObj = getYearObj(endingYear);
      const y0Section = pickBaselineYearSection(y0YearObj, isProposedMode);
      if (!isPlainObject(y0Section)) continue;

      const programBuildBudget = row?.buildBudget;

      const programConfig = getProgramConfigByDocId(docId);
      if (!programConfig) continue;

      const flatRows = buildWorkshopFlatRowsFromProgramConfig({
        programConfig,
        y2Section,
        y1Section,
        y0Section,
        y0Key,
      });
      if (!Array.isArray(flatRows) || flatRows.length === 0) continue;

      const detailEditsForDoc = detailEdits?.[docId] || {};
      const flatRowsWithOverrides = flatRows.map((r) => {
        if (r?.section) return r;
        const rowId = r?.__rowId ?? r?.label ?? null;
        const override = rowId != null ? detailEditsForDoc[String(rowId)] : undefined;
        return Number.isFinite(override) ? { ...r, buildBudget: override } : r;
      });

      const detailSum = sumDetailBuildBudget(flatRowsWithOverrides, detailEditsForDoc);

      const mismatch =
        Number.isFinite(programBuildBudget) &&
        Number.isFinite(detailSum) &&
        !nearlyEqualMoney(programBuildBudget, detailSum);

      if (mismatch) map[docId] = { mismatch: true, detailSum };
    }

    return map;
  }, [programTableRows, detailEdits, programDocs, endingYear, isProposedMode, y0Key]);

  const rowsForRenderingWithHighlights = useMemo(() => {
    return (rowsForRendering || []).map((r) => {
      const docId = r?.__docId;
      if (!docId || docId === "__TOTAL__") return r;

      const mismatch = !!programMismatchByDocId?.[docId]?.mismatch;
      if (!mismatch) return r;

      return { ...r, rowClassName: "bg-yellow-50 ring-1 ring-yellow-300" };
    });
  }, [rowsForRendering, programMismatchByDocId]);

  const getRowId = useCallback((row) => row?.__docId || row?.label, []);
  const rowIsExpandable = useCallback((row) => {
    const docId = row?.__docId;
    return !!docId && docId !== "__TOTAL__";
  }, []);

  const renderExpandedRow = useCallback(
    (row) => {
      const docId = row?.__docId;
      if (!docId || docId === "__TOTAL__") return null;

      const progDoc = programDocs?.[docId];
      if (!progDoc || !endingYear) {
        return <div className="text-sm text-gray-600 px-3 py-2">No detail data available.</div>;
      }

      const getYearObj = (y) => progDoc?.[String(y)] ?? null;

      const y2Section = getYearObj(endingYear - 2)?.Actual ?? null;

      const y1Section =
        getYearObj(endingYear - 1)?.Approved ??
        getYearObj(endingYear - 1)?.Adopted ??
        null;

      const y0YearObj = getYearObj(endingYear);
      const y0Section = pickBaselineYearSection(y0YearObj, isProposedMode);

      if (!isPlainObject(y0Section)) {
        return (
          <div className="text-sm text-gray-600 px-3 py-2">
            No detail data found for FY {endingYear} {isProposedMode ? "(Proposed)" : "(Approved)"}.
          </div>
        );
      }

      const programBuildBudget = row?.buildBudget;

      const programConfig = getProgramConfigByDocId(docId);
      if (!programConfig) {
        return (
          <div className="text-sm text-gray-600 px-3 py-2">
            No program configuration found for {docId}.
          </div>
        );
      }

      const flatRows = buildWorkshopFlatRowsFromProgramConfig({
        programConfig,
        y2Section,
        y1Section,
        y0Section,
        y0Key,
      });

      if (!flatRows.length) {
        return <div className="text-sm text-gray-600 px-3 py-2">No dollar line-items in this window.</div>;
      }

      const detailEditsForDoc = detailEdits?.[docId] || {};

      const flatRowsWithOverrides = flatRows.map((r) => {
        if (r?.section) return r;

        const rowId = r?.__rowId ?? r?.label ?? null;
        const override = rowId != null ? detailEditsForDoc[String(rowId)] : undefined;

        const nextBuildBudget = Number.isFinite(override) ? override : r?.buildBudget;
        const baselineVal = r?.[y0Key];

        const { increase, percentIncrease } = computeChangeFields(baselineVal, nextBuildBudget);

        return {
          ...r,
          buildBudget: Number.isFinite(nextBuildBudget) ? nextBuildBudget : null,
          increase,
          percentIncrease,
        };
      });

      const detailSum = sumDetailBuildBudget(flatRowsWithOverrides, detailEditsForDoc);

      const hasMismatch =
        Number.isFinite(programBuildBudget) &&
        Number.isFinite(detailSum) &&
        !nearlyEqualMoney(programBuildBudget, detailSum);

      const expandedColumns = (columns || []).map((c) => {
        const next = { ...c };

        if (next.key === "label") {
          next.label = "Item";
          next.headerAlign = "left";
          next.align = "left";
          next.render = (r) => r?.label ?? "";
        }

        return next;
      });

      const onExpandedTableChange = (e, rowLabel, colKey) => {
        if (colKey !== "buildBudget") return;

        const cleaned = parseCurrencyInput(e?.target?.value);

        const rowObj = flatRowsWithOverrides.find((x) => !x?.section && x?.label === rowLabel);
        const rowId = rowObj?.__rowId ?? rowLabel;

        setDetailEdits((prev) => {
          const next = { ...(prev || {}) };
          const prevDoc = next[docId] && typeof next[docId] === "object" ? next[docId] : {};

          next[docId] = {
            ...prevDoc,
            [String(rowId)]: isFiniteNumber(cleaned) ? cleaned : null,
          };

          return next;
        });
      };

      return (
        <div className={`px-0 py-0 ${hasMismatch ? "bg-yellow-50 ring-2 ring-yellow-300" : "bg-white"}`}>
          <CommonTable
            title={null}
            columns={expandedColumns}
            data={flatRowsWithOverrides}
            isEditing={true}
            headerColor="bg-[#d5e7f7]"
            showAggregateTotal={false}
            defaultSortField={null}
            defaultSortOrder="asc"
            enableRowExpansion={false}
            expandOnRowClick={false}
            hideColumnHeaders={true}
            onChange={onExpandedTableChange}
            editOnCellClick={true}
            commitOnBlur={true}
          />

          {hasMismatch && Number.isFinite(detailSum) ? (
            <div className="px-3 py-2 text-xs text-yellow-900">
              {(() => {
                const bb = Number.isFinite(programBuildBudget) ? programBuildBudget : null;
                const sum = detailSum;

                if (!Number.isFinite(sum) || !Number.isFinite(bb)) {
                  return (
                    <>
                      Build a Budget {Number.isFinite(bb) ? fmtDollars0(bb) : "—"} must match Sum of Items{" "}
                      {fmtDollars0(sum)}.
                    </>
                  );
                }

                const delta = Math.round(bb - sum);
                return <>Build a Budget is off by {fmtDollars0(delta)}.</>;
              })()}
            </div>
          ) : null}
        </div>
      );
    },
    [programDocs, endingYear, isProposedMode, columns, y0Key, detailEdits]
  );

const onExport = useCallback(() => {
  if (!hasWindow) return;

  exportWorkshopToExcel({
    columns,
    rows: rowsForRenderingWithHighlights, // Programs sheet
    title,
    endingYear,
    y0Status,

    // Details sheet inputs
    programDocs,
    isProposedMode,
    y0Key,
    detailEdits,
  });
}, [
  hasWindow,
  columns,
  rowsForRenderingWithHighlights,
  title,
  endingYear,
  y0Status,
  programDocs,
  isProposedMode,
  y0Key,
  detailEdits,
]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center" aria-modal="true" role="dialog">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative z-[9999] w-[min(1100px,94vw)] max-h-[88vh] overflow-auto rounded-lg bg-white shadow-xl border border-gray-200">
        {/* Header */}
        <div className="px-4 border-b border-gray-200 h-14 flex items-center">
          {/* Left: Export */}
          <div className="h-10 w-10 flex items-center justify-center">
            <button
              type="button"
              className="h-10 w-10 inline-flex items-center justify-center rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent"
              onClick={onExport}
              aria-label="Export to Excel"
              title="Export to Excel"
              disabled={loading || !!error || !hasWindow}
            >
              <ArrowDownTrayIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="flex-1 text-center">
            <div className="text-lg font-semibold text-gray-900 leading-none">{title}</div>
          </div>

          {/* Right: Close */}
          <button
            type="button"
            className="h-10 w-10 inline-flex items-center justify-center rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            onClick={onClose}
            aria-label="Close modal"
            title="Close"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="p-3 space-y-2">
          {loading ? (
            <div className="text-sm text-gray-600">Loading program-level budget tables…</div>
          ) : error ? (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>
          ) : !hasWindow ? (
            <div className="text-sm text-gray-600">
              Program budget data is not available for the selected window.
            </div>
          ) : (
            <CommonTable
              title={null}
              columns={columns}
              data={rowsForRenderingWithHighlights}
              isEditing={true}
              headerColor="bg-[#d5e7f7]"
              showAggregateTotal={false}
              defaultSortField={null}
              defaultSortOrder="asc"
              onChange={onChange}
              editOnCellClick={true}
              commitOnBlur={true}
              getRowId={getRowId}
              rowIsExpandable={rowIsExpandable}
              expandedRowIds={expandedPrograms}
              setExpandedRowIds={setExpandedPrograms}
              expandOnLabelClick={true}
              showExpandCaret={true}
              enableRowExpansion={true}
              renderExpandedRow={renderExpandedRow}
              expandedRowCellClassName="p-0 bg-white"
              expandedRowRowClassName="border-none"
              expandedRowCellStyle={{ padding: 0 }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
